import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SignInModal from '../components/SignInModal';
import { ArrowLeft, Download, Eye, Calendar, User, Tag, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ResourceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  /**
   * Select the safest Preview file from resource.files array
   * Priority: PDF > Student-named > NOT Answer Key
   */
  const selectPreviewFile = (files) => {
    if (!files || files.length === 0) return null;

    // Keywords to EXCLUDE (Answer Key indicators)
    const excludeKeywords = ['answer', 'answers', 'key', 'teacher', 'solution', 'solutions'];
    
    // Keywords to PREFER (Student-facing indicators)
    const preferKeywords = ['student', 'worksheet', 'pack'];

    // Filter out Answer Key files (CRITICAL SAFETY CHECK)
    const safeFiles = files.filter(file => {
      const nameLower = (file.name || '').toLowerCase();
      return !excludeKeywords.some(keyword => nameLower.includes(keyword));
    });

    if (safeFiles.length === 0) {
      // NO safe files found - do NOT expose any file as preview
      return null;
    }

    // Step 1: Find PDFs that contain "student" keywords
    const studentPDFs = safeFiles.filter(file => {
      const nameLower = (file.name || '').toLowerCase();
      const isPDF = file.type === 'pdf' || nameLower.endsWith('.pdf');
      const isStudent = preferKeywords.some(keyword => nameLower.includes(keyword));
      return isPDF && isStudent;
    });

    if (studentPDFs.length > 0) {
      return studentPDFs[0]; // Best match: Student PDF
    }

    // Step 2: Find ANY PDF from safe files
    const anyPDF = safeFiles.find(file => {
      const nameLower = (file.name || '').toLowerCase();
      return file.type === 'pdf' || nameLower.endsWith('.pdf');
    });

    if (anyPDF) {
      return anyPDF;
    }

    // Step 3: No PDFs found - return first safe file (or null)
    return safeFiles[0] || null;
  };

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setCheckingAuth(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!checkingAuth) {
      if (!user) {
        // User not signed in - show modal
        setShowSignInModal(true);
        setLoading(false);
      } else {
        // User signed in - fetch resource
        fetchResource();
      }
    }
  }, [id, user, checkingAuth]);

  const fetchResource = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/resources/${id}`);
      setResource(response.data);
    } catch (error) {
      console.error('Error fetching resource:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || checkingAuth) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navigation />
        <div className="flex items-center justify-center py-40">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#0EA5E9] mx-auto mb-4"></div>
            <p className="text-lg text-gray-600">Loading resource...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show sign-in modal if user not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navigation />
        <SignInModal 
          isOpen={showSignInModal} 
          onClose={() => navigate('/browse')}
          redirectUrl={`/resources/${id}`}
        />
        <div className="flex items-center justify-center py-40">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Sign In Required</h2>
            <p className="text-gray-600 mb-6">Please sign in to view this resource</p>
            <button
              onClick={() => navigate('/browse')}
              className="bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all rounded-lg px-6 py-3"
            >
              Back to Browse
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navigation />
        <div className="flex items-center justify-center py-40">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Resource not found</h2>
            <button
              onClick={() => navigate('/browse')}
              className="bg-[#0EA5E9] text-white hover:bg-[#0284C7] transition-all duration-300 rounded-full font-bold shadow-[0_4px_0_0_#0284C7] hover:shadow-[0_2px_0_0_#0284C7] hover:translate-y-[2px] px-8 py-3 inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Browse
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/browse')}
            data-testid="back-btn"
            className="mb-8 text-[#0EA5E9] hover:text-[#0284C7] font-semibold inline-flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Browse
          </button>
          
          <div className="bg-white rounded-3xl p-8 sm:p-12 border-4 border-gray-100 shadow-xl">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="text-sm font-medium tracking-wide text-[#0EA5E9] bg-[#0EA5E9]/10 px-4 py-2 rounded-full">
                {resource.category.replace('_', ' ')}
              </span>
              <span className="text-sm font-medium tracking-wide text-[#FB7185] bg-[#FB7185]/10 px-4 py-2 rounded-full">
                Grade {resource.grade_level}
              </span>
              {resource.subject && (
                <span className="text-sm font-medium tracking-wide text-[#34D399] bg-[#34D399]/10 px-4 py-2 rounded-full">
                  {resource.subject}
                </span>
              )}
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-gray-900 mb-6" data-testid="resource-title">
              {resource.title}
            </h1>
            
            <p className="text-base sm:text-lg leading-relaxed text-gray-600 mb-8">
              {resource.description}
            </p>
            
            {resource.thumbnail_url && (
              <div className="rounded-2xl overflow-hidden mb-8">
                <img 
                  src={resource.thumbnail_url} 
                  alt={resource.title}
                  className="w-full h-auto"
                />
              </div>
            )}
            
            {resource.content && (
              <div className="prose max-w-none mb-8">
                <div className="text-base leading-relaxed text-gray-700 whitespace-pre-wrap">
                  {resource.content}
                </div>
              </div>
            )}
            
            {resource.tags && resource.tags.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {resource.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* SAFE PREVIEW BUTTON - Uses intelligent file selection */}
            {(() => {
              // Try to select safe preview file from files array
              const previewFile = selectPreviewFile(resource.files);
              
              // Fallback to legacy file_url (for backward compatibility)
              const previewUrl = previewFile?.url || resource.file_url;

              return previewUrl ? (
                <div className="flex gap-4 mb-8">
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="view-btn"
                    className="bg-[#0EA5E9] text-white hover:bg-[#0284C7] transition-all duration-300 rounded-full font-bold shadow-[0_4px_0_0_#0284C7] hover:shadow-[0_2px_0_0_#0284C7] hover:translate-y-[2px] px-8 py-3 inline-flex items-center gap-2"
                  >
                    <Eye className="w-5 h-5" />
                    Preview {previewFile ? `(${previewFile.name})` : ''}
                  </a>
                  <a
                    href={previewUrl}
                    download
                    data-testid="download-btn"
                    className="bg-[#FDE047] text-[#1F2937] hover:bg-[#EAB308] transition-all duration-300 rounded-full font-bold shadow-[0_4px_0_0_#EAB308] hover:shadow-[0_2px_0_0_#EAB308] hover:translate-y-[2px] px-8 py-3 inline-flex items-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Download
                  </a>
                </div>
              ) : null;
            })()}

            {/* All Files Section */}
            {resource.files && resource.files.length > 0 && (
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 mb-8 border-2 border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    All Files ({resource.files.length})
                  </h3>
                  <button
                    onClick={() => {
                      resource.files.forEach((file, index) => {
                        setTimeout(() => {
                          const link = document.createElement('a');
                          link.href = file.url;
                          link.download = file.name || `file-${index + 1}`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }, index * 200);
                      });
                    }}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transition-all rounded-lg px-4 py-2 text-sm font-bold inline-flex items-center gap-2 shadow-md hover:shadow-lg"
                  >
                    <Download className="w-4 h-4" />
                    Download All
                  </button>
                </div>
                <div className="grid gap-3">
                  {resource.files.map((file, index) => {
                    // Check if this file looks like an Answer Key
                    const nameLower = (file.name || '').toLowerCase();
                    const isAnswerKey = ['answer', 'answers', 'key', 'teacher', 'solution'].some(
                      keyword => nameLower.includes(keyword)
                    );

                    return (
                      <div key={index} className="bg-white rounded-xl p-4 flex items-center justify-between border border-gray-200 hover:border-blue-300 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`rounded-lg p-2 ${isAnswerKey ? 'bg-amber-100' : 'bg-blue-100'}`}>
                            <FileText className={`w-5 h-5 ${isAnswerKey ? 'text-amber-600' : 'text-blue-600'}`} />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {file.name || `File ${index + 1}`}
                              {isAnswerKey && (
                                <span className="ml-2 text-xs font-medium text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                                  Teacher Resource
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-500">
                              {file.type ? file.type.toUpperCase() : 'FILE'} 
                              {file.size && ` • ${(file.size / 1024).toFixed(1)} KB`}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-blue-600 text-white hover:bg-blue-700 transition-all rounded-lg px-4 py-2 text-sm font-semibold inline-flex items-center gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </a>
                          <a
                            href={file.url}
                            download={file.name || `file-${index + 1}`}
                            className="bg-yellow-400 text-gray-900 hover:bg-yellow-500 transition-all rounded-lg px-4 py-2 text-sm font-semibold inline-flex items-center gap-2"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            
            <div className="border-t-2 border-gray-100 pt-6 mt-8">
              <div className="flex flex-wrap gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Created {new Date(resource.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default ResourceDetail;
